/**
 * A photograph made small enough to send over a store's mobile line, before it is sent.
 *
 * <p>Action evidence went up as the phone took it: four to ten megabytes, uploaded while the
 * person waited with the panel locked. An audit photograph is about half a megabyte, because
 * the in-page camera stops at 1080p (camera.ts) — which is why one felt instant and the other
 * did not (test team, 4 November). A receipt or a mended shelf reads perfectly well at 1920
 * pixels on the long side; the extra pixels were only time.</p>
 *
 * <p>Only the photo types the server takes, and only when there is something to gain. A
 * PDF, a spreadsheet or a photo already small goes as it is, and so does anything the browser
 * cannot decode — the original is always a correct answer, just a slower one.</p>
 */

/** The long side a photograph is brought down to. */
export const LongEdge = 1920;

/** Below this a photograph is sent untouched: re-encoding it would save little and cost a generation. */
export const SmallEnough = 1.5 * 1024 * 1024;

/** JPEG quality for the copy. Text on an invoice stays sharp; the file is a tenth of the original. */
export const Quality = 0.85;

const Shrinkable = new Set(['image/jpeg', 'image/png', 'image/webp']);

/** The size a w×h image is drawn at so that neither side passes the long edge. */
export function fitWithin(width: number, height: number, edge = LongEdge): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= edge) return { width, height };

  const scale = edge / longest;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/** Whether a file of this type and size is worth decoding at all. */
export function worthShrinking(type: string, size: number): boolean {
  return Shrinkable.has(type.split(';')[0]!.trim().toLowerCase()) && size > SmallEnough;
}

/**
 * The photograph at most {@link LongEdge} on its long side, as a JPEG; or the file itself
 * when it is not a photo, already small, or could not be decoded here.
 */
export async function shrinkPhoto(file: File): Promise<File> {
  if (!worthShrinking(file.type, file.size)) return file;

  let bitmap: ImageBitmap | null = null;
  try {
    // from-image so a portrait taken on a phone stays portrait: the orientation lives in
    // the EXIF the copy will not carry.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const { width, height } = fitWithin(bitmap.width, bitmap.height);

    const canvas = new OffscreenCanvas(width, height);
    const context = canvas.getContext('2d');
    if (!context) return file;
    context.drawImage(bitmap, 0, 0, width, height);

    const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: Quality });

    // A copy that came out no smaller is not a better answer.
    if (blob.size >= file.size) return file;

    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg', lastModified: file.lastModified });
  } catch {
    return file;
  } finally {
    bitmap?.close();
  }
}
